// Parent "selected child" scope — adapted from useActiveTenantScope.js's
// localStorage + custom-event pattern. Unlike tenant scope, this is never
// injected as a request header: every parent API call takes the studentId
// directly in the URL, and the backend re-verifies the parent-student link
// on every request (see parentAccess.service.js) — this hook only persists
// which child is selected across navigation/reloads, it grants no access.
import { useCallback, useEffect, useState } from "react";

const ACTIVE_CHILD_ID_KEY = "activeChildId";
const ACTIVE_CHILD_NAME_KEY = "activeChildName";
export const ACTIVE_CHILD_EVENT = "gapsmania:active-child-change";

function dispatchChange() {
  window.dispatchEvent(new CustomEvent(ACTIVE_CHILD_EVENT));
}

export function getActiveChildContext() {
  try {
    return {
      childId: localStorage.getItem(ACTIVE_CHILD_ID_KEY) || null,
      childName: localStorage.getItem(ACTIVE_CHILD_NAME_KEY) || "",
    };
  } catch {
    return { childId: null, childName: "" };
  }
}

export function setActiveChildContext(childId, childName = "") {
  try {
    if (childId) {
      localStorage.setItem(ACTIVE_CHILD_ID_KEY, childId);
      localStorage.setItem(ACTIVE_CHILD_NAME_KEY, childName);
    } else {
      localStorage.removeItem(ACTIVE_CHILD_ID_KEY);
      localStorage.removeItem(ACTIVE_CHILD_NAME_KEY);
    }
  } catch {
    // localStorage unavailable (e.g. private browsing) — scope just won't persist.
  }
  dispatchChange();
}

export default function useActiveChildScope() {
  const [scope, setScope] = useState(() => getActiveChildContext());

  useEffect(() => {
    const sync = () => setScope(getActiveChildContext());
    window.addEventListener("storage", sync);
    window.addEventListener(ACTIVE_CHILD_EVENT, sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener(ACTIVE_CHILD_EVENT, sync);
    };
  }, []);

  const selectChild = useCallback((childId, childName = "") => {
    setActiveChildContext(childId, childName);
  }, []);

  return { ...scope, selectChild };
}
